<template>
  <div class="portal-flyout" :class="{ display }">
    <div class="portal-flyout-panel">
      <div class="portal-flyout-header">
        <h2 class="portal-flyout-title">{{ $t("airportTable.editflight.title") }}</h2>
        <button
          type="button"
          class="portal-flyout-close"
          :aria-label="$t('airportTable.editflight.cancelbtn')"
          @click="toggleEditActiveFlyout()"
        >
          <span class="mdi mdi-close" aria-hidden="true"></span>
        </button>
      </div>
      <div class="portal-flyout-body">
        <div class="flyout-field" :data-id="flightOverride.id">
          <div class="flyout-field-label">{{ $t("airportTable.editflight.id") }}</div>
          <div class="flyout-field-value">{{ flightOverride.id }}</div>
        </div>
        <div class="flyout-field">
          <div class="flyout-field-label">{{ $t("airportTable.editflight.flight") }}</div>
          <div class="flyout-field-value flyout-flight-line">
            <span class="airline-logo-slot">
              <img
                v-if="airlineLogoUrl(flightOverride.airlineCode)"
                class="airline-logo"
                :src="airlineLogoUrl(flightOverride.airlineCode)"
                :alt="flightOverride.airlineCode + ' logo'"
                loading="lazy"
                @error="onAirlineLogoError"
              />
            </span>
            <span class="flyout-flight-text">
              {{ formatAirlineName(flightOverride.airlineName || flightOverride.airline) }}
              ({{ flightOverride.airlineCode }}) {{ flightOverride.flightNumber }}
            </span>
          </div>
        </div>
        <div class="flyout-field">
          <div class="flyout-field-label">{{ $t("airportTable.editflight.partners") }}</div>
          <div class="flyout-field-value flyout-partners-list">
            <template v-if="hasCodeshares">
              <div
                v-for="(codeshare, index) in flightOverride.codeshares"
                :key="'partner-' + index"
                class="flyout-partner-line"
              >
                <span class="airline-logo-slot airline-logo-slot--partner">
                  <img
                    v-if="airlineLogoUrl(codeshare.airlineCode)"
                    class="airline-logo"
                    :src="airlineLogoUrl(codeshare.airlineCode)"
                    :alt="codeshare.airlineCode + ' logo'"
                    loading="lazy"
                    @error="onAirlineLogoError"
                  />
                </span>
                <span class="flyout-partner-text">
                  <span class="flyout-partner-designator">{{ formatFlightDesignator(codeshare.airlineCode, codeshare.flightNumber) }}</span>
                  <span v-if="codesharePartnerName(codeshare)" class="flyout-partner-name">{{ codesharePartnerName(codeshare) }}</span>
                </span>
              </div>
            </template>
            <span v-else class="flyout-partner-empty">—</span>
          </div>
        </div>
        <div class="flyout-field">
          <div class="flyout-field-label">{{ $t("airportTable.editflight.a/d") }}</div>
          <div class="flyout-field-value">{{ capitalize(flightOverride.flightType) }}</div>
        </div>
        <div class="flyout-field">
          <div class="flyout-field-label">{{ $t("airportTable.editflight.city") }}</div>
          <div class="flyout-field-value city-with-flag">
            <span class="city-flag-slot">
              <img
                v-if="countryFlagUrl"
                class="country-flag"
                :src="countryFlagUrl"
                :alt="countryCode + ' flag'"
                width="24"
                height="18"
              />
            </span>
            <span class="city-label">{{ capitalize(flightOverride.airportCity || flightOverride.city) }} ({{ flightOverride.airportCode || flightOverride.cityCode }})</span>
          </div>
        </div>
        <div class="flyout-field">
          <div class="flyout-field-label">{{ $t("airportTable.editflight.state") }}</div>
          <div class="flyout-field-value state">{{ flightOverride.airportState }}</div>
        </div>
        <!-- <div class="i-row">
          <div class="key">Estimated:</div>
          <div class="fake-input" :class="{'focus' : displayDateAndTimeSelector }" @click="displayDateAndTimeSelector = true">{{ flightOverride.overrides.estimated ? flightOverride.overrides.estimated : flightOverride.estimated }}</div>
        </div> -->
        <div class="flyout-field">
          <div class="flyout-field-label">{{ $t("airportTable.editflight.estimated") }}</div>
          <div class="flyout-field-control">
            <date-picker class="flyout-date-picker" :key="'est-null-'+renderIndex" v-model="selectedDate" type="datetime" :format="dateFormat" v-if="flightOverride.overrides.estimated == null" :placeholder="flightOverride.estimated"></date-picker>
            <date-picker class="flyout-date-picker" :key="'est-ovr-'+renderIndex" v-model="selectedDate" type="datetime" :format="dateFormat" v-else :placeholder="flightOverride.overrides.estimated"></date-picker>
          </div>
        </div>

        <div class="flyout-field">
          <div class="flyout-field-label">Boarding:</div>
          <div class="flyout-field-control">
            <date-picker class="flyout-date-picker" :key="'brd-null-'+renderIndex" v-model="boardingDate" type="datetime" :format="dateFormat" v-if="flightOverride.overrides.boarding == null" :placeholder="flightOverride.boarding ? flightOverride.boarding : defaultDate"></date-picker>
            <date-picker class="flyout-date-picker" :key="'brd-ovr-'+renderIndex" v-model="boardingDate" type="datetime" :format="dateFormat" v-else :placeholder="flightOverride.overrides.boarding"></date-picker>
          </div>
        </div>

        <div class="flyout-field">
          <div class="flyout-field-label">{{ $t("airportTable.editflight.gate") }}</div>
          <div class="flyout-field-control">
            <Select :options="gateOptions" :selected="gateSelected" @update="overrideGate" :key="renderIndex"/>
          </div>
        </div>
        <div class="flyout-field">
          <div class="flyout-field-label">{{ $t("airportTable.editflight.claim") }}</div>
          <div class="flyout-field-control">
            <Select :options="claimOptions" :selected="claimSelected" @update="overrideClaim" :key="renderIndex"/>
          </div>
        </div>
        <!--div class="i-row">
          <div class="key">Check-In:</div>
          <div class="value">{{ flightOverride.checkIn }}</div>
        </div-->
        <div class="flyout-field">
          <div class="flyout-field-label">{{ $t("airportTable.editflight.status") }}</div>
          <div class="flyout-field-control">
            <Select :options="statusOptions" :selected="statusSelected" @update="overrideStatus" :key="renderIndex"/>
          </div>
        </div>
        <!--div class="i-row">
          <div class="key">Languages:</div>
          <Input type="text" :value="flightOverride.overrides.languages ? flightOverride.overrides.languages : flightOverride.languages" @update="overrideLanguage"/>
        </div-->
        <div class="flyout-field">
          <div class="flyout-field-label">{{ $t("airportTable.editflight.remarks") }}</div>
          <div class="flyout-field-control">
            <TextArea :rows="6" :value="flightOverride.overrides.remarks ? flightOverride.overrides.remarks : flightOverride.remarks" @update="overrideRemarks" :key="renderIndex" />
          </div>
        </div>

        <div class="flyout-field flyout-actions-row">
          <Button :text="$t('airportTable.editflight.restorebtn')" type="danger" @click.native="revertChanges()"/>
        </div>

      </div>
      <div class="portal-flyout-footer">
        <Button :text="$t('airportTable.editflight.cancelbtn')" type="tertiary" @click.native="toggleEditActiveFlyout()" />
        <Button :text="$t('airportTable.editflight.savebtn')" type="primary" :disabled="submitting" @click.native="saveOverrideFlight()"/>
      </div>
    </div>
    
    <!-- <DateTimeSelector v-if="displayDateAndTimeSelector" :date="flightOverride.overrides.estimated ? flightOverride.overrides.estimated : flightOverride.estimated" title="Edit Estimated Flight Time" :subtitle="'Flight ' + flight.flightNumber" @update="overrideScheduledTime" @dismiss="dismissDateTimeSelector"/> -->
  
  </div>
</template>

<script>

import Button from './Button.vue';
import Select from './Select.vue';
import TextArea from './TextArea.vue';
// import DateTimeSelector from './DateTimeSelector.vue';
import ActiveFlights from '../js/services/ActiveFlights';
import { decode } from 'html-entities';
import { FlightOverride } from '../js/models/FlightOverride';
import { getFlightCountryFlagUrl, resolveFlightCountryCode } from '../js/utils/countryFlag';
import { getNpmAirlineLogoUrl, preloadAirlineLogos } from '../js/utils/airlineLogo';
import { mapGetters } from 'vuex';
import moment from 'moment';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
// Removed flightStatusOrder.json dependency; rely on backend-provided sortOrder

export default {
  name: 'FlyoutEditActiveFlight',
  components: {
    Button,
    Select,
    TextArea,
    // DateTimeSelector,
    DatePicker
  },
  props: {
    flight: Object
  },
  computed: {
    ...mapGetters({
      gates: 'airportSettings/gates',
      claims: 'airportSettings/claims',
      statuses: 'airportSettings/statuses'
    }),
    // Removed filteredStatuses/currentStatusOrder; now we filter by direction and rely on sortOrder
    // Computed wrappers for options with logging of base vs overrides
    gateSelected() {
      const sel = (this.flightOverride && this.flightOverride.overrides && this.flightOverride.overrides.gate) || this.flightOverride.gate;
      try { console.log('[FlyoutEditActiveFlight] gateSelected', { base: this.flightOverride.gate, override: this.flightOverride.overrides.gate, selected: sel }); } catch(e){ /* no-op */ }
      return sel;
    },
    gateOptions() {
      const include = this.gateSelected;
      const opts = this.formatSelectOptions(this.gates || [], null, include);
      try { console.log('[FlyoutEditActiveFlight] gateOptions', { includeKey: include, options: (opts||[]).map(o=>o&&o.value) }); } catch(e){ /* no-op */ }
      return opts;
    },
    claimSelected() {
      const sel = (this.flightOverride && this.flightOverride.overrides && this.flightOverride.overrides.claim) || this.flightOverride.claim;
      try { console.log('[FlyoutEditActiveFlight] claimSelected', { base: this.flightOverride.claim, override: this.flightOverride.overrides.claim, selected: sel }); } catch(e){ /* no-op */ }
      return sel;
    },
    claimOptions() {
      const include = this.claimSelected;
      const opts = this.formatSelectOptions(this.claims || [], null, include);
      try { console.log('[FlyoutEditActiveFlight] claimOptions', { includeKey: include, options: (opts||[]).map(o=>o&&o.value) }); } catch(e){ /* no-op */ }
      return opts;
    },
    statusSelected() {
      const sel = (this.flightOverride && this.flightOverride.overrides && this.flightOverride.overrides.status) || this.flightOverride.status;
      try { console.log('[FlyoutEditActiveFlight] statusSelected', { base: this.flightOverride.status, override: this.flightOverride.overrides.status, selected: sel }); } catch(e){ /* no-op */ }
      return sel;
    },
    statusOptions() {
      const include = this.statusSelected;
      const all = Array.isArray(this.statuses) ? this.statuses : [];
      const ftRaw = (this.flightOverride && this.flightOverride.flightType) ? String(this.flightOverride.flightType) : '';
      const ftr = ftRaw.toLowerCase();
      const isDep = ftr.startsWith('d') || ftr.includes('dep');
      const isArr = ftr.startsWith('a') || ftr.includes('arr');
      const ft = isDep ? 'departure' : (isArr ? 'arrival' : 'departure');
      // 1) Filter by direction (include 'both'); normalize casing and default missing to 'both'
      const byDirection = all.filter(s => {
        if (!s) return false;
        const dir = (s.direction ? String(s.direction) : 'both').toLowerCase();
        return dir === 'both' || dir === ft;
      });
      // 2) Build options using backend sortOrder only (no custom order file)
      const opts = this.formatSelectOptions(byDirection, null, include);
      try { console.log('[FlyoutEditActiveFlight] statusOptions', { flightType: ft, includeKey: include, inputCount: all.length, filteredCount: byDirection.length, options: (opts||[]).map(o=>o&&o.value) }); } catch(e){ /* no-op */ }
      return opts;
    },
    countryCode() {
      return resolveFlightCountryCode(this.flightOverride);
    },
    countryFlagUrl() {
      return getFlightCountryFlagUrl(this.flightOverride);
    },
    hasCodeshares() {
      return Array.isArray(this.flightOverride.codeshares) && this.flightOverride.codeshares.length > 0;
    }
  },
  watch: {
    // When a new flight is opened for edit, reinitialize internal state
    flight(newFlight) {
      try { console.log('[FlyoutEditActiveFlight] watch.flight changed', { id: newFlight && newFlight.id }); } catch(e){ /* no-op */ }
      this.flightOverride = JSON.parse(JSON.stringify(newFlight || {}));
      this.initialOverrides = null;
      this.initialEffective = null;
      this.snapshotTaken = false;
      this.selectedDate = '';
      this.boardingDate = '';
      this.renderIndex += 1;
      // Recapture snapshots after reinit
      this.$nextTick(() => {
        this.captureInitialOverrides();
        this.tryCaptureInitialEffective();
      });
    },
    // When options arrive from the store, attempt capturing effective snapshot
    gates() { this.tryCaptureInitialEffective(); },
    claims() { this.tryCaptureInitialEffective(); },
    statuses() { this.tryCaptureInitialEffective(); }
  },
  data() {
    return {
      display: false,
      displayDateAndTimeSelector: false,
      submitting: false,
      flightOverride: JSON.parse(JSON.stringify(this.flight)),
      initialOverrides: null,
      initialEffective: null,
      snapshotTaken: false,
      renderIndex: 0,
      selectedDate: '',
      boardingDate: '',
      defaultDate: '',
      dateFormat: 'YYYY-MM-DD hh:mm a',
      selectedDateTime: '',
      boardingDateTime: '',
      airlineLogosReady: false
    };
  },
  mounted() {
    preloadAirlineLogos().then(() => {
      this.airlineLogosReady = true;
    });
    window.setTimeout( () => { this.display = true; }, 10);
    let today = new Date();
    this.defaultDate = moment(today).format(this.dateFormat);
    this.captureInitialOverrides();
    // Defer effective snapshot until options are loaded (gates/claims/statuses from store)
    this.tryCaptureInitialEffective();
    // Logging initial state
    try {
      console.log('[FlyoutEditActiveFlight] mounted', {
        id: this.flight && this.flight.id,
        base: {
          estimated: this.flight && this.flight.estimated,
          boarding: this.flight && this.flight.boarding,
          gate: this.flight && this.flight.gate,
          claim: this.flight && this.flight.claim,
          status: this.flight && this.flight.status,
          remarks: this.flight && this.flight.remarks
        },
        overrides: this.flight && this.flight.overrides,
        snapshot: this.initialOverrides,
        effectiveSnapshot: this.initialEffective
      });
    } catch(e) { console.warn('[FlyoutEditActiveFlight] mount log error', e); }
  },
  methods: {
    optionsReady() {
      return Array.isArray(this.gates) && this.gates.length &&
             Array.isArray(this.claims) && this.claims.length &&
             Array.isArray(this.statuses) && this.statuses.length;
    },
    captureInitialOverrides() {
      try {
        this.initialOverrides = new FlightOverride(this.flightOverride);
        console.log('[FlyoutEditActiveFlight] snapshot initialOverrides', this.initialOverrides);
      } catch(e) { console.warn('[FlyoutEditActiveFlight] initial snapshot error', e); }
    },
    tryCaptureInitialEffective() {
      if (this.snapshotTaken) return;
      if (!this.optionsReady()) {
        // Try again on next tick; watchers will also retry when data arrives
        this.$nextTick(() => this.tryCaptureInitialEffective());
        return;
      }
      try {
        this.initialEffective = {
          estimated: this.flightOverride.overrides.estimated != null ? this.flightOverride.overrides.estimated : this.flightOverride.estimated,
          boarding: this.flightOverride.overrides.boarding != null ? this.flightOverride.overrides.boarding : this.flightOverride.boarding,
          gate: this.gateSelected,
          claim: this.claimSelected,
          checkIn: this.flightOverride.overrides.checkIn != null ? this.flightOverride.overrides.checkIn : this.flightOverride.checkIn,
          status: this.statusSelected,
          languages: this.flightOverride.overrides.languages != null ? this.flightOverride.overrides.languages : this.flightOverride.languages,
          remarks: this.flightOverride.overrides.remarks != null ? this.flightOverride.overrides.remarks : this.flightOverride.remarks
        };
        this.snapshotTaken = true;
        console.log('[FlyoutEditActiveFlight] snapshot initialEffective (deferred)', this.initialEffective);
      } catch(e) { console.warn('[FlyoutEditActiveFlight] initial effective snapshot error', e); }
    },
    // dismissDateTimeSelector() {
    //   this.displayDateAndTimeSelector = false;
    // },
    formatSelectOptions(opts, customOrder = null, includeKey = null) {
      const orderIndex = (key) => {
        if (Array.isArray(customOrder) && customOrder.length) {
          const idx = customOrder.indexOf(String(key));
          return idx === -1 ? Number.POSITIVE_INFINITY : idx;
        }
        return undefined;
      };
      const includeSet = new Set();
      let options = opts.map(opt => {
        if (!opt) return false;
        const isInclude = includeKey != null && String(opt.key) === String(includeKey);
        if (opt.isActive == 1 || isInclude) {
          const ord = orderIndex(opt.key);
          includeSet.add(String(opt.key));
          return { value: opt.key, name: opt.name, order: (ord !== undefined ? ord : opt.sortOrder) };
        }
        return false;
      }).filter(Boolean);
      // If includeKey was requested but not found in the provided list, add a fallback option with the raw key label
      if (includeKey != null && !includeSet.has(String(includeKey))) {
        const ord = orderIndex(includeKey);
        options.unshift({ value: includeKey, name: String(includeKey), order: (ord !== undefined ? ord : Number.NEGATIVE_INFINITY) });
      }
      const sorted = options.sort((a, b) => a.order - b.order);
      try {
        console.log('[FlyoutEditActiveFlight] formatSelectOptions', {
          includeKey,
          inputCount: Array.isArray(opts) ? opts.length : 'n/a',
          sample: (opts && opts[0]) ? { key: opts[0].key, name: opts[0].name, isActive: opts[0].isActive } : 'n/a',
          returnedKeys: sorted.map(o => o && o.value)
        });
      } catch(e) { /* no-op */ }
      return sorted;
    },
    capitalize(str) {
      return str != null ? decode(str.toLowerCase()) : 'N/A'; // CSS then makes this Capitalize
    },
    airlineLogoUrl(code) {
      return getNpmAirlineLogoUrl(code);
    },
    onAirlineLogoError(e) {
      if (e && e.target) e.target.style.visibility = 'hidden';
    },
    formatAirlineName(airlineName) {
      if (airlineName == null || airlineName === '') return '—';
      return String(airlineName).replace(/([A-Z]+)/g, ' $1').replace(/^ /, '');
    },
    formatFlightDesignator(code, number) {
      const c = code != null ? String(code).trim() : '';
      const n = number != null ? String(number).trim() : '';
      if (c && n) return `${c} ${n}`;
      return c || n || '—';
    },
    codesharePartnerName(codeshare) {
      const name = codeshare.airlineName || codeshare.airline;
      if (name) return this.formatAirlineName(name);
      return '';
    },
    toggleEditActiveFlyout() {
      this.$emit('toggleEditActiveFlyout', null);
    },
    // overrideScheduledTime(value) {
    //   this.flightOverride.overrides.estimated = value;
    //   this.dismissDateTimeSelector();
    // },
    overrideGate(value) {
      this.flightOverride.overrides.gate = value;
      try { console.log('[FlyoutEditActiveFlight] overrideGate', { selected: value }); } catch(e){ /* no-op */ }
    },
    overrideClaim(value) {
      this.flightOverride.overrides.claim = value;
      try { console.log('[FlyoutEditActiveFlight] overrideClaim', { selected: value }); } catch(e){ /* no-op */ }
    },
    overrideStatus(value) {
      this.flightOverride.overrides.status = value;
      try { console.log('[FlyoutEditActiveFlight] overrideStatus', { selected: value }); } catch(e){ /* no-op */ }
    },
    overrideRemarks(value) {
      this.flightOverride.overrides.remarks = value;
      try { console.log('[FlyoutEditActiveFlight] overrideRemarks', { value }); } catch(e){ /* no-op */ }
    },
    revertChanges() {
      let revert = null;
      try {
        // Prefer restoring to what the user initially SAW (effective). If not available, use raw overrides snapshot, then default restore.
        if (this.initialEffective) {
          revert = new FlightOverride(this.flightOverride);
          revert.estimated = this.initialEffective.estimated != null ? this.initialEffective.estimated : null;
          revert.boarding = this.initialEffective.boarding != null ? this.initialEffective.boarding : null;
          revert.gate = this.initialEffective.gate != null ? this.initialEffective.gate : null;
          revert.claim = this.initialEffective.claim != null ? this.initialEffective.claim : null;
          revert.checkIn = this.initialEffective.checkIn != null ? this.initialEffective.checkIn : null;
          revert.status = this.initialEffective.status != null ? this.initialEffective.status : null;
          revert.languages = this.initialEffective.languages != null ? this.initialEffective.languages : null;
          revert.remarks = this.initialEffective.remarks != null ? this.initialEffective.remarks : null;
        } else if (this.initialOverrides) {
          revert = JSON.parse(JSON.stringify(this.initialOverrides));
        } else {
          revert = new FlightOverride(this.flightOverride);
          revert.restore();
        }
      } catch(e) {
        revert = new FlightOverride(this.flightOverride);
        revert.restore();
      }
      try {
        console.log('[FlyoutEditActiveFlight] revertChanges BEFORE', {
          base: {
            estimated: this.flightOverride.estimated,
            boarding: this.flightOverride.boarding,
            gate: this.flightOverride.gate,
            claim: this.flightOverride.claim,
            status: this.flightOverride.status,
            remarks: this.flightOverride.remarks
          },
          overrides: this.flightOverride.overrides
        });
      } catch(e){ /* no-op */ }
      this.flightOverride.overrides = revert;
      // clear local models so date pickers and other inputs do not retain stale values
      this.selectedDate = '';
      this.boardingDate = '';
      this.renderIndex += 1; // this forces all components with a key of 'renderIndex' to rerender to force prop updates
      try {
        console.log('[FlyoutEditActiveFlight] revertChanges AFTER', {
          overrides: this.flightOverride.overrides,
          selectedDate: this.selectedDate,
          boardingDate: this.boardingDate,
          renderIndex: this.renderIndex
        });
      } catch(e){ /* no-op */ }
    },
    saveOverrideFlight() {
      let history = this.flightOverride.overrides.estimated;
      let boardingHistory = this.flightOverride.overrides.boarding;
      let userSelectedDate = moment(this.selectedDate).format('YYYY-MM-DD');
      let userSelectedTime = moment(this.selectedDate).format('hh:mm a');
      let boardingSelectedDate = moment(this.boardingDate).format('YYYY-MM-DD');
      let boardingSelectedTime = moment(this.boardingDate).format('hh:mm a');
      this.selectedDateTime = userSelectedDate + ' ' + userSelectedTime;  
      this.boardingDateTime = boardingSelectedDate + ' ' + boardingSelectedTime;  
      
      if (this.selectedDateTime == 'Invalid date Invalid date') {
        if (history != null) {
          this.flightOverride.overrides.estimated = history;
        } else {
          // leave as null to respect restored defaults
          this.flightOverride.overrides.estimated = null;
        }
      } else {
        this.flightOverride.overrides.estimated = this.selectedDateTime;
      }

      if (this.boardingDateTime == 'Invalid date Invalid date') {
        if (boardingHistory != null) {
          this.flightOverride.overrides.boarding = boardingHistory;
        } else {
          // leave as null to respect restored defaults
          this.flightOverride.overrides.boarding = null;
        }
      } else {
        this.flightOverride.overrides.boarding = this.boardingDateTime;
      }

      try {
        console.log('[FlyoutEditActiveFlight] saveOverrideFlight BEFORE payload', {
          overrides: this.flightOverride.overrides,
          base: {
            estimated: this.flightOverride.estimated,
            boarding: this.flightOverride.boarding,
            gate: this.flightOverride.gate,
            claim: this.flightOverride.claim,
            status: this.flightOverride.status,
            remarks: this.flightOverride.remarks
          }
        });
      } catch(e){ /* no-op */ }
      let payload = new FlightOverride(this.flightOverride);
      this.submitting = true;
      new ActiveFlights().editFlights(payload)
      .then( () => {
        this.toggleEditActiveFlyout();
        this.$store.commit('activeFlights/flightOverride', this.flightOverride);
        try { console.log('[FlyoutEditActiveFlight] saveOverrideFlight SUCCESS'); } catch(e){ /* no-op */ }
      })
      .catch(err => {
        console.log(err);
        try { console.log('[FlyoutEditActiveFlight] saveOverrideFlight ERROR', err); } catch(e){ /* no-op */ }
      })
      .then(() => {
        this.submitting = false;
        try { console.log('[FlyoutEditActiveFlight] saveOverrideFlight DONE'); } catch(e){ /* no-op */ }
      });
      
    }
  }
};
</script>

<style scoped lang="scss">
.portal-flyout-close {
  border: none;
  background: transparent;
  padding: 0;
}

.flyout-date-picker {
  width: 100%;
}

::v-deep .flyout-date-picker {
  width: 100%;

  .mx-input {
    width: 100%;
    height: 36px;
    padding: 6px 30px 6px 10px;
    font-size: 13px;
    color: $general-black-light;
    background-color: $general-white;
    border: 1px solid rgba($brand-primary, 0.25);
    border-radius: 8px;
    box-shadow: none;
    transition: border-color 0.2s, box-shadow 0.2s;

    &:hover {
      border-color: rgba($brand-primary, 0.45);
    }

    &:focus {
      border-color: $brand-primary;
      box-shadow: 0 0 0 3px rgba($brand-primary, 0.15);
    }
  }
}

::v-deep .flyout-field-control {
  .select select {
    border-radius: 8px;
    border-color: rgba($brand-primary, 0.25);
    font-size: 13px;

    &:focus {
      border-color: $brand-primary;
      box-shadow: 0 0 0 3px rgba($brand-primary, 0.15);
    }
  }

  textarea {
    width: 100%;
    max-width: 100%;
    resize: vertical;
    min-height: 96px;
    padding: 10px 12px;
    font-size: 13px;
    color: $general-black-light;
    border: 1px solid rgba($brand-primary, 0.25);
    border-radius: 8px;

    &:focus {
      outline: none;
      border-color: $brand-primary;
      box-shadow: 0 0 0 3px rgba($brand-primary, 0.15);
    }
  }
}

.city-with-flag {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  min-height: 21px;
  line-height: 21px;

  .city-flag-slot {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-shrink: 0;
    width: 28px;
    min-width: 28px;
    height: 21px;
    line-height: 0;
  }

  .country-flag {
    display: block;
    width: 28px;
    height: 21px;
    margin: 0;
    object-fit: cover;
    border-radius: 2px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
  }

  .city-label {
    line-height: 21px;
    white-space: nowrap;
  }
}

.flyout-flight-line,
.flyout-partner-line {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;
}

.flyout-partners-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.flyout-flight-text,
.flyout-partner-text {
  line-height: 1.4;
  text-transform: capitalize;
}

.flyout-partner-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.flyout-partner-designator {
  font-weight: 600;
  font-size: 12px;
  color: $brand-primary;
}

.flyout-partner-name {
  font-size: 12px;
  color: rgba($brand-primary-dark, 0.88);
}

.flyout-partner-empty {
  color: rgba($general-black-light, 0.6);
}

.airline-logo-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  min-width: 44px;
  height: 32px;
  padding: 3px 4px;
  box-sizing: border-box;
  border-radius: 6px;
  overflow: hidden;
  line-height: 0;
  background: $general-white;
  border: 1px solid hsl(211, 30%, 88%);
  box-shadow: 0 1px 2px rgba($brand-primary-dark, 0.08);

  &--partner {
    width: 36px;
    min-width: 36px;
    height: 26px;
    padding: 2px 3px;
  }
}

.airline-logo {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  object-position: center center;
}
</style>
